import {
  Button,
  Card,
  CardHeader,
  FormField,
  MultiValueInput,
  Select,
  TextInput,
} from "@constructorfleet/ui/design-system";
import type { BhgResultTrigger } from "@constructorfleet/bhg-product-core";
import { useConfirmAction } from "./confirm-action";

export function ResultTriggerEditor({
  resultRules,
  setResultRules,
  openIndex,
  setOpenIndex,
}: {
  resultRules: BhgResultTrigger[];
  setResultRules: React.Dispatch<React.SetStateAction<BhgResultTrigger[]>>;
  openIndex: number | null;
  setOpenIndex: (index: number | null) => void;
}) {
  const { requestConfirm, confirmModal } = useConfirmAction();

  return (
    <div className="ds-stack">
      {confirmModal}
      {resultRules.map((rule, index) => {
        const isOpen = openIndex === index;

        return (
          <Card key={`result-rule-${index}`}>
            <CardHeader
              title={rule.label || rule.id || `Result ${index + 1}`}
              meta={
                <>
                  {rule.id}
                  {rule.isScheduledTrigger ? " · scheduled" : ""}
                  {rule.isTechDifficultyAlert ? " · tech alert" : ""} {isOpen ? "▲" : "▼"}
                </>
              }
              action={
                isOpen ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="btn-danger"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestConfirm({
                        title: "Delete result rule",
                        message: `Delete "${rule.label || rule.id || `Result ${index + 1}`}"? This cannot be undone until you save.`,
                        confirmLabel: "Delete",
                        tone: "danger",
                        onConfirm: () => {
                          setResultRules((current) => current.filter((_, i) => i !== index));
                          setOpenIndex(null);
                        },
                      });
                    }}
                  >
                    Delete
                  </Button>
                ) : undefined
              }
            />
            <div
              className="card-hdr"
              style={{ cursor: "pointer" }}
              onClick={() => setOpenIndex(isOpen ? null : index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setOpenIndex(isOpen ? null : index);
              }}
              hidden
            />
            {isOpen ? (
              <div className="card-body ds-stack">
                <div className="settings-compact-grid">
                  <FormField label="Result ID" htmlFor={`result-id-${index}`}>
                    <TextInput
                      id={`result-id-${index}`}
                      value={rule.id}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setResultRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, id: nextValue } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField label="Label" htmlFor={`result-label-${index}`}>
                    <TextInput
                      id={`result-label-${index}`}
                      value={rule.label}
                      onChange={(event) => {
                        const nextValue = event.currentTarget.value;
                        setResultRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, label: nextValue } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField label="Priority" htmlFor={`result-priority-${index}`}>
                    <TextInput
                      id={`result-priority-${index}`}
                      type="number"
                      value={String(rule.priority)}
                      onChange={(event) => {
                        const nextValue = Number(event.currentTarget.value || 0);
                        setResultRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, priority: nextValue } : item
                          )
                        );
                      }}
                    />
                  </FormField>
                  <FormField label="Scheduled trigger" htmlFor={`result-is-scheduled-${index}`}>
                    <Select
                      id={`result-is-scheduled-${index}`}
                      value={rule.isScheduledTrigger ? "true" : "false"}
                      onChange={(event) => {
                        const nextValue = event.target.value === "true";
                        setResultRules((current) =>
                          current.map((item, i) =>
                            i === index ? { ...item, isScheduledTrigger: nextValue } : item
                          )
                        );
                      }}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </Select>
                  </FormField>
                  <FormField
                    label="Tech difficulty alert"
                    htmlFor={`result-tech-difficulty-${index}`}
                    helpText="When matched, broadcast a technical-difficulty notice to all enrolled users at the same clinic."
                  >
                    <Select
                      id={`result-tech-difficulty-${index}`}
                      value={rule.isTechDifficultyAlert ? "true" : "false"}
                      onChange={(event) => {
                        const nextValue = event.target.value === "true";
                        setResultRules((current) =>
                          current.map((item, i) =>
                            i === index
                              ? { ...item, isTechDifficultyAlert: nextValue || undefined }
                              : item
                          )
                        );
                      }}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes — broadcast tech difficulty alert</option>
                    </Select>
                  </FormField>
                  <FormField
                    label="Result date offset (days)"
                    htmlFor={`result-offset-n-days-${index}`}
                  >
                    <TextInput
                      id={`result-offset-n-days-${index}`}
                      type="number"
                      value={
                        typeof rule.offsetNDays === "number" && Number.isFinite(rule.offsetNDays)
                          ? String(rule.offsetNDays)
                          : ""
                      }
                      onChange={(event) => {
                        const raw = event.currentTarget.value;
                        const parsed = raw.trim().length > 0 ? Number(raw) : NaN;
                        setResultRules((current) =>
                          current.map((item, i) =>
                            i === index
                              ? {
                                  ...item,
                                  offsetNDays:
                                    Number.isFinite(parsed) && !Number.isNaN(parsed)
                                      ? parsed
                                      : undefined,
                                }
                              : item
                          )
                        );
                      }}
                    />
                  </FormField>
                </div>

                <FormField label="Result phrases" htmlFor={`result-phrases-${index}`}>
                  <MultiValueInput
                    id={`result-phrases-${index}`}
                    values={rule.phrasePatterns}
                    onChange={(nextValues) =>
                      setResultRules((current) =>
                        current.map((item, i) =>
                          i === index ? { ...item, phrasePatterns: nextValues } : item
                        )
                      )
                    }
                    addButtonLabel="Add result phrase"
                    itemAriaLabel={(pi) =>
                      `Result phrase ${pi + 1} for ${rule.id || `result ${index + 1}`}`
                    }
                    minItems={1}
                  />
                </FormField>
              </div>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
}
